import { useState } from 'react';
import { FileImage } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import type { PcmpProposalDetail } from '../../types/pcmpProposal.types';
import { PcmpProposalSection } from './PcmpProposalSection';

interface PcmpAttachmentsCardProps {
  attachments: PcmpProposalDetail['attachments'];
}

type Attachment = PcmpProposalDetail['attachments'][number];

const isImage = (attachment: Attachment): boolean =>
  attachment.contentType === null || attachment.contentType.startsWith('image/');

const ATTACHMENT_KIND_LABELS: Record<string, string> = {
  MOBILE_PROOF: 'Proof',
};

export function PcmpAttachmentsCard({ attachments }: PcmpAttachmentsCardProps) {
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const viewable = attachments.filter((attachment) => attachment.url !== null);
  if (viewable.length === 0) return null;

  return (
    <PcmpProposalSection title="Attachments">
      <div className="flex flex-wrap gap-3">
        {viewable.map((attachment) => {
          const url = attachment.url ?? '';
          const label = ATTACHMENT_KIND_LABELS[attachment.kind] ?? attachment.kind;
          return isImage(attachment) ? (
            <button
              key={attachment.id}
              type="button"
              onClick={() => setOpenUrl(url)}
              className="flex flex-col items-center gap-1 rounded-lg border p-1 hover:bg-muted"
            >
              <img src={url} alt={label} className="size-24 rounded object-cover" />
              <span className="text-xs text-muted-foreground">{label}</span>
            </button>
          ) : (
            <a
              key={attachment.id}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="flex size-24 flex-col items-center justify-center gap-1 rounded-lg border text-xs hover:bg-muted"
            >
              <FileImage className="size-6 text-muted-foreground" aria-hidden />
              {label}
            </a>
          );
        })}
      </div>
      <Dialog open={openUrl !== null} onOpenChange={(open) => !open && setOpenUrl(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogTitle className="sr-only">Attachment</DialogTitle>
          {openUrl ? (
            <img src={openUrl} alt="Attachment" className="max-h-[80vh] w-full object-contain" />
          ) : null}
        </DialogContent>
      </Dialog>
    </PcmpProposalSection>
  );
}
